import { useTranslation } from '@/hooks/useTranslation';
import type { Trace } from '@/features/traceability/type/Tool';
import { formatDuration, formatDistance, formatVolume } from '@/utils/format';
import { lubricationTypes } from '@/features/traceability/data/lubricationOptions';
import { clampingTypes } from '@/features/traceability/data/clampingOptions';
import { toolHolderTypes } from '@/features/traceability/data/toolHolderOptions';
import { Badge } from '@/components/ui/badge';
import { ToolStateBadge } from '@/components/shared/ToolStateBadge';
import { cn } from '@/lib/utils';
import { SectionHeader } from './TraceEditPrimitives';

type TraceViewBodyProps = {
  trace: Trace;
  showEmpty: boolean;
};

type RowDef = {
  label: string;
  value: string | number | null | undefined;
  isEmpty?: boolean;
};

type SectionDef = {
  key: string;
  title: string;
  rows: RowDef[];
};

function ValueRow({ label, value, isEmpty }: RowDef) {
  const { t } = useTranslation();
  const displayValue = value !== null && value !== undefined && value !== '' ? value : null;
  return (
    <div
      className={cn('grid grid-cols-[10rem_1fr] gap-2 px-4 py-1.5 border-b border-border/40 last:border-0')}
      data-empty={isEmpty ? 'true' : undefined}
    >
      <span className="text-sm text-muted-foreground truncate">{label}</span>
      <span className="font-mono text-sm text-foreground break-words">
        {displayValue !== null ? (
          displayValue
        ) : (
          <span className="italic text-muted-foreground/60">
            {t('toolView.metadata.fallback.notProvided')}
          </span>
        )}
      </span>
    </div>
  );
}

function findLabel(
  options: { value: string; labelKey: string }[],
  value: string | null | undefined,
  t: (key: string) => string
): string | null {
  if (!value) return null;
  const option = options.find((o) => o.value === value);
  return option ? t(option.labelKey) : value;
}

export function TraceViewBody({ trace, showEmpty }: TraceViewBodyProps) {
  const { t } = useTranslation();

  const lubLabel = findLabel(lubricationTypes, trace.lubType, t);
  const clampLabel = findLabel(clampingTypes, trace.clampingType, t);
  const holderLabel = findLabel(toolHolderTypes, trace.holderType, t);

  const materialBreadcrumb = trace.material?.name ?? null;

  const sections: SectionDef[] = [
    {
      key: 'general',
      title: t('toolView.advanced.sections.general'),
      rows: [
        { label: t('toolView.advanced.detail.operator'), value: trace.operator },
        { label: t('toolView.advanced.detail.operation'), value: trace.operation },
        { label: t('toolView.advanced.detail.machine'), value: undefined }, // placeholder — replaced below
        {
          label: t('toolView.advanced.detail.totalDuration'),
          value: formatDuration(trace.totalMachiningDuration),
        },
        {
          label: t('toolView.advanced.detail.pieceDuration'),
          value: formatDuration(trace.pieceMachiningDuration),
        },
        { label: t('toolView.advanced.detail.pieces'), value: trace.machinedPieces },
        {
          label: t('toolView.advanced.detail.volume'),
          value: formatVolume(trace.machinedVolume),
        },
        {
          label: t('toolView.advanced.detail.distance'),
          value: formatDistance(trace.machinedDistance),
        },
        { label: t('toolView.advanced.detail.malround'), value: trace.malround },
        { label: 'Vc', value: trace.vc },
        { label: 'Fz', value: trace.fz },
        { label: 'ap', value: trace.ap },
        { label: 'ae', value: trace.ae },
      ],
    },
    {
      key: 'machine',
      title: t('toolView.advanced.sections.machine'),
      rows: [
        { label: t('toolView.advanced.detail.machine'), value: trace.machine?.name },
        { label: t('toolView.advanced.detail.machineBrand'), value: trace.machine?.brand },
        { label: t('toolView.advanced.detail.machineModel'), value: trace.machine?.model },
        { label: t('toolView.advanced.detail.machineSerial'), value: trace.machine?.serialNumber },
        { label: t('toolView.advanced.detail.machineKafkaName'), value: trace.machine?.kafkaName },
      ],
    },
    {
      key: 'material',
      title: t('toolView.advanced.sections.material'),
      rows: [
        { label: t('toolView.advanced.detail.material'), value: materialBreadcrumb },
      ],
    },
    {
      key: 'lubrication',
      title: t('toolView.advanced.sections.lubrication'),
      rows: [
        { label: t('toolView.advanced.detail.lubType'), value: lubLabel },
        { label: t('toolView.advanced.detail.lubBrand'), value: trace.lubBrand },
      ],
    },
    {
      key: 'clamping',
      title: t('toolView.advanced.sections.clamping'),
      rows: [
        { label: t('toolView.advanced.detail.clampingType'), value: clampLabel },
        { label: t('toolView.advanced.detail.clampingBrand'), value: trace.clampingBrand },
      ],
    },
    {
      key: 'holder',
      title: t('toolView.advanced.sections.holder'),
      rows: [
        { label: t('toolView.advanced.detail.holderType'), value: holderLabel },
        { label: t('toolView.advanced.detail.holderBrand'), value: trace.holderBrand },
      ],
    },
  ];

  const changeReasons = trace.toolState?.changeReason ?? [];
  const toolStateValue = trace.toolState?.toolState ?? 'unknown';

  return (
    <div className="divide-y divide-border/30">
      {/* General — operator row replaced with operation below */}
      {sections.map((section) => {
        const rows = section.rows.filter(
          (row) => showEmpty || (row.value !== null && row.value !== undefined && row.value !== '')
        );
        if (!showEmpty && rows.length === 0) return null;
        return (
          <div key={section.key}>
            <SectionHeader title={section.title} />
            {rows.map((row) => (
              <ValueRow
                key={row.label}
                label={row.label}
                value={row.value as string | number | null}
                isEmpty={
                  row.value === null || row.value === undefined || row.value === ''
                }
              />
            ))}
          </div>
        );
      })}

      {/* Break / State section */}
      {(showEmpty || toolStateValue || changeReasons.length > 0 || trace.toolState?.note) && (
        <div>
          <SectionHeader title={t('toolView.advanced.sections.breakState')} />

          {/* Tool state pill row */}
          {(showEmpty || toolStateValue) && (
            <div className="grid grid-cols-[10rem_1fr] gap-2 px-4 py-1.5 border-b border-border/40">
              <span className="text-sm text-muted-foreground">{t('toolView.advanced.detail.toolState')}</span>
              <div>
                <ToolStateBadge toolState={toolStateValue} />
              </div>
            </div>
          )}

          {/* Change reasons chips row */}
          {(showEmpty || changeReasons.length > 0) && (
            <div
              className="grid grid-cols-[10rem_1fr] gap-2 px-4 py-1.5 border-b border-border/40"
              data-empty={changeReasons.length === 0 ? 'true' : undefined}
            >
              <span className="text-sm text-muted-foreground">
                {t('toolView.advanced.detail.changeReasons')}
              </span>
              <div className="flex flex-wrap gap-1">
                {changeReasons.length > 0 ? (
                  changeReasons.map((reason) => (
                    <Badge key={reason}>{reason}</Badge>
                  ))
                ) : (
                  <span className="italic text-muted-foreground/60 text-sm font-mono">
                    {t('toolView.metadata.fallback.notProvided')}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Note row */}
          {(showEmpty || trace.toolState?.note) && (
            <ValueRow
              label={t('toolView.advanced.detail.toolNote')}
              value={trace.toolState?.note}
              isEmpty={!trace.toolState?.note}
            />
          )}
        </div>
      )}
    </div>
  );
}

